/**
 * Branded underwriting model summary (.xlsx) from a saved scenario.
 * D2D Energy navy/amber theme · zero-dep OOXML + store ZIP.
 */
import { downloadBytes, zipStore } from './ooxmlZip.js';

function xmlEscape(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cellRef(r, c) {
  let col = '';
  let n = c;
  while (n >= 0) {
    col = String.fromCharCode((n % 26) + 65) + col;
    n = Math.floor(n / 26) - 1;
  }
  return `${col}${r + 1}`;
}

/** @param {*} value @param {number|null} style */
function cellXml(r, c, value, style = null) {
  const ref = cellRef(r, c);
  const sAttr = style != null ? ` s="${style}"` : '';
  if (value == null || value === '') {
    return `<c r="${ref}"${sAttr}/>`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"${sAttr}><v>${value}</v></c>`;
  }
  if (typeof value === 'boolean') {
    return `<c r="${ref}"${sAttr} t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  return `<c r="${ref}"${sAttr} t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;
}

/**
 * rows: array of arrays, or array of { values, styles? }
 * styles: parallel array of style indices (optional)
 */
function sheetXml(rows) {
  const normalized = rows.map((row) =>
    Array.isArray(row) ? { values: row, styles: [] } : row,
  );
  const maxC = normalized.reduce((m, row) => Math.max(m, row.values.length), 0);
  const sheetData = normalized
    .map((row, r) => {
      const cells = [];
      for (let c = 0; c < row.values.length; c += 1) {
        cells.push(cellXml(r, c, row.values[c], row.styles?.[c] ?? null));
      }
      return `<row r="${r + 1}">${cells.join('')}</row>`;
    })
    .join('');
  const dim = `A1:${cellRef(Math.max(normalized.length - 1, 0), Math.max(maxC - 1, 0))}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="${dim}"/>
  <sheetData>${sheetData}</sheetData>
</worksheet>`;
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return null;
  return +(v * 100).toFixed(2);
}

const S = {
  brandTitle: 1,
  brandSub: 2,
  header: 3,
  label: 4,
  muted: 5,
};

function brandCoverRows(freeze) {
  return [
    { values: ['D2D Energy · Eagle East TX VDR'], styles: [S.brandTitle] },
    { values: ['Underwriting summary (saved scenario)'], styles: [S.brandSub] },
    { values: [] },
    { values: ['Confidential', freeze.confidentiality], styles: [S.label, null] },
    { values: ['Scenario name', freeze.label], styles: [S.label, null] },
    { values: ['Scenario ID', freeze.scenarioId], styles: [S.label, null] },
    { values: ['Content hash', freeze.contentHash], styles: [S.label, null] },
    { values: ['Saved at', freeze.frozenAt], styles: [S.label, null] },
    { values: ['Approver', freeze.approver || ''], styles: [S.label, null] },
    { values: ['Note', freeze.note || ''], styles: [S.label, null] },
    { values: ['DealPackage generator', freeze.generatorVersion || ''], styles: [S.label, null] },
    { values: ['As-of', freeze.asOf || ''], styles: [S.label, null] },
    { values: [] },
    {
      values: ['Trust rule', 'Circulate named saved scenarios only — desk knobs remain editable in-app.'],
      styles: [S.label, S.muted],
    },
    {
      values: ['Class', 'Model = computed; Judgment = desk input; Fact = seller reference.'],
      styles: [S.label, S.muted],
    },
    {
      values: ['Prepared by', 'Data2Dollars.ai'],
      styles: [S.label, null],
    },
  ];
}

function headerRow(cols) {
  return { values: cols, styles: cols.map(() => S.header) };
}

/** Buyer overhead categories sheet — desk judgment, not seller fact. */
function buyerOverheadRows(freeze) {
  const oh = freeze.assumptions?.buyerOverhead;
  const b = freeze.results?.base;
  const rows = [
    headerRow(['Category', '$/mo', 'In CF', 'Class']),
    ...(oh?.categories || []).map((c) => [c.label, c.monthly ?? 0, !!c.includeInCf, 'Judgment']),
    [],
    ['Total monthly', oh?.monthlyTotal ?? 0, '$/mo', 'Judgment'],
    ['Included in CF', oh?.monthlyIncluded ?? 0, '$/mo', 'Judgment'],
    ['PV impact ($MM)', b?.buyerOhPvDeltaMM ?? null, '', 'Computed'],
    ['IRR w/o buyer OH (%)', pct(b?.irrWithoutBuyerOh), '', 'Computed'],
    [],
    ['Note', oh?.illustrationNote || 'Buyer G&A categories are desk judgment, not seller facts.'],
  ];
  return rows;
}

/** Liability schedule sheet — negative-PV / inactive wells, never floored to zero. */
function liabilityScheduleRows(freeze) {
  const l = freeze.liabilitySchedule;
  const rows = [
    headerRow(['Lease', 'Oneline PV', 'Model asset PV', 'Plug/ARO share ($MM)', 'Inactive', 'Note']),
    ...(l?.rows || []).map((r) => [
      r.lease,
      r.onelinePv ?? null,
      r.modelAssetPv ?? null,
      r.suggestedPlugAroShareMM ?? null,
      !!r.inactive,
      r.regulatoryNote || '',
    ]),
    [],
    ['Row count', l?.rowCount ?? 0],
    ['Oneline negative total', l?.onelineNegativeTotal ?? 0],
    ['Model negative total', l?.modelNegativeTotal ?? 0],
    ['Floors negatives', false],
    [
      'Note',
      'Liability schedule is separate from operating PV. Negative wells are not floored to zero in package totals.',
    ],
  ];
  return rows;
}

/** Build sheet row matrices from a freeze. */
export function underwritingSummarySheets(freeze) {
  const a = freeze.assumptions;
  const b = freeze.results.base;
  const s = freeze.results.stress;

  const assumptions = [
    headerRow(['Knob', 'Value', 'Unit / note', 'Class']),
    ['Bid price', a.bidPriceMM, '$MM', 'Judgment'],
    ['WTI', a.wti ?? null, '$/bbl', 'Judgment'],
    ['HH', a.hh, '$/MMBtu', 'Judgment'],
    ['Boswell / concentration vol adj', a.boswellVolAdj, 'fraction', 'Judgment'],
    ['Netback adj', a.netbackAdj, '$/unit', 'Judgment'],
    ['Discount rate', a.discountRate, 'fraction', 'Judgment'],
    ['ARO / P&A', a.aroMM, '$MM', 'Judgment'],
    ['Overhead in CF', a.overheadInCf, 'boolean', 'Judgment'],
    ['Overhead monthly', a.overheadMonthly, '$/mo', 'Judgment'],
    ['Primary product', a.primaryProduct ?? '', '', 'Fact'],
    ['Multi-product', a.multiProduct ? 'yes' : 'no', '', 'Fact'],
    ['Products', Array.isArray(a.products) ? a.products.join(', ') : '', '', 'Fact'],
    [],
    ['Seller ask (working)', freeze.seller?.askMM ?? null, '$MM', 'Fact'],
    ['Oneline PDP PV10', freeze.seller?.refPdpPv10MM ?? null, '$MM', 'Fact'],
    ['Oneline total PV10', freeze.seller?.refTotalPv10MM ?? null, '$MM', 'Fact'],
  ];

  const results = [
    headerRow(['Metric', 'Base', 'Stress', 'Class']),
    ['PV label', b?.pvLabel ?? '', s?.pvLabel ?? '', 'Model'],
    ['Gross PV ($MM)', b?.pvMM ?? null, s?.pvMM ?? null, 'Model'],
    ['After ARO ($MM)', b?.bidBridgePvMM ?? null, s?.bidBridgePvMM ?? null, 'Model'],
    ['NTM CF ($MM)', b?.ntmCfMM ?? null, s?.ntmCfMM ?? null, 'Model'],
    ['Undisc CF ($MM)', b?.undiscCfMM ?? null, s?.undiscCfMM ?? null, 'Model'],
    ['IRR @ bid (%)', pct(b?.irrAnnual), pct(s?.irrAnnual), 'Computed'],
    ['MOIC @ bid (before P&A)', b?.moic ?? null, s?.moic ?? null, 'Computed'],
    ['Model-basis share', b?.boswellShare ?? null, s?.boswellShare ?? null, 'Model'],
    ['4× NTM ($MM)', b?.multiples?.x4 ?? null, s?.multiples?.x4 ?? null, 'Back-check'],
    ['5× NTM ($MM)', b?.multiples?.x5 ?? null, s?.multiples?.x5 ?? null, 'Back-check'],
    ['6× NTM ($MM)', b?.multiples?.x6 ?? null, s?.multiples?.x6 ?? null, 'Back-check'],
    [],
    [
      'Desk stress definition',
      a.primaryProduct === 'oil'
        ? a.multiProduct
          ? 'WTI −$5 · concentration −25% · netback stress · overhead forced in CF (HH held at package gas strip unless scenario moves it)'
          : 'WTI −$5 · concentration −25% · netback stress · overhead forced in CF'
        : 'HH −$0.50 · Boswell −25% · netback −$0.15 · overhead forced in CF (not packaged named bear)',
    ],
  ];

  const bridge = [
    headerRow(['Step', 'Value ($MM)', 'Class']),
    [`Gross ${b?.pvLabel || 'PV'}`, b?.pvMM ?? null, 'Model'],
    ['− ARO / P&A', a.aroMM, 'Judgment'],
    ['Equity / asset PV', b?.bidBridgePvMM ?? null, 'Model'],
    ['Your bid', a.bidPriceMM, 'Judgment'],
    ['IRR @ bid (%)', pct(b?.irrAnnual), 'Computed'],
    ['MOIC @ bid (before P&A)', b?.moic ?? null, 'Computed'],
  ];

  const scenarios = [
    headerRow(['ID', 'Label', 'PV ($MM)', 'After ARO ($MM)', 'NTM CF ($MM)', 'IRR @ bid', '5× ($MM)']),
    ...(freeze.results.namedScenarios || []).map((r) => [
      r.id,
      r.label,
      r.pvMM,
      r.bridgeMM,
      r.ntmCfMM,
      r.irrAnnual == null ? '' : pct(r.irrAnnual),
      r.x5MM,
    ]),
  ];

  const cal = freeze.calibration || {};
  const ms = freeze.midstream || {};
  const fidelity = [
    headerRow(['Check', 'Value']),
    ['Recommendation', freeze.recommendation || ''],
    ['Model base PV10 ($MM)', cal.modelBasePv10MM ?? ''],
    ['Seller PDP PV10 ($MM)', cal.refPdpPv10MM ?? ''],
    ['Model − seller ($MM)', cal.modelVsRefPdpMM ?? ''],
    ['cfScale median', cal.cfScaleMedian ?? ''],
    ['cfScale Boswell', cal.cfScaleBoswell ?? ''],
    ['cfScale outliers', cal.cfScaleOutliers ?? ''],
    [],
    ['Midstream status', ms.overallStatus || ''],
    ['Contracts open', ms.openCount ?? ''],
    ['Contracts total', ms.contractCount ?? ''],
    ['L12M realized ($/Mcf)', ms.l12Realized ?? ''],
    ['Stack @ 6k ($/MMBtu)', ms.stackAt6k ?? ''],
  ];

  const dca = freeze.boswellDca || {};
  const boswellDca = [
    headerRow(['Metric', 'Value', 'Class']),
    ['Trough (Mcf/d)', dca.troughMcfpd ?? '', 'Fact'],
    ['Post-acid (Mcf/d)', dca.postAcidMcfpd ?? '', 'Fact'],
    ['Fitted De (%/yr)', dca.fittedDePct ?? '', 'Model'],
    ['Fit source', dca.fitSource ?? '', 'Model'],
    ['Hyperbolic b', dca.hyperbolicB ?? '', 'Model'],
    ['Hyperbolic R²', dca.hyperbolicR2 ?? '', 'Model'],
    ['Holdout MAPE (%)', dca.holdoutMapePct ?? '', 'Model'],
    ['Fade window (mo)', dca.fadeMonths ?? '', 'Judgment'],
    ['Fade source', dca.fadeSource ?? '', 'Model'],
    ['Locked boswellVolAdj', dca.volAdj ?? '', 'Judgment'],
    ['Fit-ready', dca.fitReady == null ? '' : dca.fitReady, 'Model'],
    ['Flowing fraction', dca.uptimeFraction ?? '', 'Fact'],
    ['WGR (Bbl/MMcf)', dca.wgrBblPerMmcf ?? '', 'Fact'],
    ['Avg FTP (psi)', dca.avgFtp ?? '', 'Fact'],
    [],
    [
      'Rule',
      'boswellVolAdj is the only volume judgment; DCA never re-tunes cfScale (short post-acid windows cannot prove durability alone).',
    ],
  ];

  return [
    { name: 'Cover', rows: brandCoverRows(freeze) },
    { name: 'Buyer overhead', rows: buyerOverheadRows(freeze) },
    { name: 'Liabilities', rows: liabilityScheduleRows(freeze) },
    { name: 'Assumptions', rows: assumptions },
    { name: 'Results', rows: results },
    { name: 'Bid bridge', rows: bridge },
    { name: 'Named scenarios', rows: scenarios },
    { name: 'Boswell DCA', rows: boswellDca },
    { name: 'Fidelity', rows: fidelity },
  ];
}

/** Register workbook — one branded sheet of saved scenarios. */
export function buildRegisterXlsx(freezes) {
  const rows = [
    { values: ['D2D Energy · Eagle East TX VDR'], styles: [S.brandTitle] },
    { values: ['Named scenario register'], styles: [S.brandSub] },
    { values: [] },
    headerRow([
      'Name',
      'Scenario ID',
      'Hash',
      'Saved at',
      'Bid ($MM)',
      'HH',
      'Boswell adj',
      'Base IRR %',
      'Stress IRR %',
      'Approver',
    ]),
    ...freezes.map((f) => [
      f.label,
      f.scenarioId,
      f.contentHash,
      f.frozenAt,
      f.assumptions.bidPriceMM,
      f.assumptions.hh,
      f.assumptions.boswellVolAdj,
      pct(f.results.base?.irrAnnual),
      pct(f.results.stress?.irrAnnual),
      f.approver || '',
    ]),
  ];
  return buildWorkbook([{ name: 'Register', rows }]);
}

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="4">
    <font><sz val="11"/><color theme="1"/><name val="Calibri"/></font>
    <font><b/><sz val="16"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><b/><sz val="12"/><color rgb="FF0C1F2E"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
  </fonts>
  <fills count="5">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF0C1F2E"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE8A317"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF163A4F"/></patternFill></fill>
  </fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf/></cellStyleXfs>
  <cellXfs count="6">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="2" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="3" fillId="4" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyFont="1"><alignment horizontal="left"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
  </cellXfs>
</styleSheet>`;

function buildWorkbook(sheets) {
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheets
    .map(
      (_, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join('\n  ')}
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    ${sheets
      .map(
        (s, i) =>
          `<sheet name="${xmlEscape(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`,
      )
      .join('\n    ')}
  </sheets>
</workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${sheets
    .map(
      (_, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
    )
    .join('\n  ')}
  <Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  return zipStore([
    { name: '[Content_Types].xml', data: contentTypes },
    { name: '_rels/.rels', data: rootRels },
    { name: 'xl/workbook.xml', data: workbook },
    { name: 'xl/_rels/workbook.xml.rels', data: workbookRels },
    { name: 'xl/styles.xml', data: STYLES_XML },
    ...sheets.map((s, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: sheetXml(s.rows),
    })),
  ]);
}

/** @returns {Uint8Array} .xlsx bytes */
export function buildUnderwritingXlsx(freeze) {
  return buildWorkbook(underwritingSummarySheets(freeze));
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function downloadUnderwritingXlsx(freeze) {
  downloadBytes(`${freeze.scenarioId}-underwriting.xlsx`, buildUnderwritingXlsx(freeze), XLSX_MIME);
}

export function downloadRegisterXlsx(freezes) {
  downloadBytes('eagle-scenario-register.xlsx', buildRegisterXlsx(freezes), XLSX_MIME);
}

/** Low-level builders reused by the asset-workflow DCA/EUR workbook (src/assets/workflowExport.js). */
export const xlsxParts = { buildWorkbook, headerRow, S, XLSX_MIME };
